import { useState, useEffect } from 'react';
import type { RepoStackAnalysis } from '../types';
import { api } from '../../../services/authenticatedApi';

interface UseRepoStackAnalysisResult {
  data: RepoStackAnalysis | null;
  loading: boolean;
  error: string | null;
}

/** Fetches the KG stack analysis for a given job from the backend. */
export function useRepoStackAnalysis(jobId: string | undefined): UseRepoStackAnalysisResult {
  const [data, setData] = useState<RepoStackAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchGraph = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get<RepoStackAnalysis>(`/analysis/${jobId}/graph`);
        if (!cancelled) {
          setData(response.data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load KG data';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchGraph();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  return { data, loading, error };
}
